/**
 * desktopPageCredential — the one rule for what the /desktop shell hands its
 * embedded page to reach the workstation with (2026-10-04). Imports nothing, so
 * the rule is tested on its own and every caller asks it the same way.
 *
 * The page holds only what the shell hands it and cannot fall back by itself, so
 * "nothing" means every desktop call is refused and the page retries for ever.
 * That is what happened to every Prest0 Inc. member from 2026-09-27: the
 * machine's keys moved to the server-only record (S1b-3), so no browser could
 * read a key any more, and the machine's /health could not be read from
 * prest0.ai, so the shell never asked for a ticket either.
 *
 * In order:
 *   1. a personal ticket, where the machine says it takes one (S1b-2c step 2);
 *   2. else the machine key, read fresh, as before;
 *   3. with no key at all, a ticket anyway, unless the machine answered that it
 *      takes none. A ticket can only be refused; no credential always is.
 * null only when all three found nothing.
 */
export type PageCredentialSources = {
  /** A ticket for the page, or null. `noKey`: step 3, there is no key to hand instead. */
  ticket: (noKey: boolean) => Promise<string | null>;
  /** The machine key, or null where no browser may read one. */
  key: () => Promise<string | null>;
};

export async function decidePageCredential(src: PageCredentialSources): Promise<string | null> {
  const ticket = await src.ticket(false).catch(() => null);
  if (ticket) return ticket;
  const key = await src.key().catch(() => null);
  if (key) return key;
  return src.ticket(true).catch(() => null);
}
